import { CutSetup, ElasticRoundAnalyticsWithAveragePoints } from '@spikemark/rest-api';
import { useCallback, useMemo } from 'react';

export const useCut = ({
  leaderboardData,
  cutSetup,
  type,
}: {
  leaderboardData?: ElasticRoundAnalyticsWithAveragePoints[];
  cutSetup?: CutSetup;
  type: 'team' | 'player';
}) => {
  const noAutoTeams = cutSetup?.cutMode === 'AUTO' && type === 'team' && cutSetup?.nTopTeams === 0;
  const noAutoPlayers =
    cutSetup?.cutMode === 'AUTO' && type === 'player' && cutSetup?.nTopPlayers === 0;
  const noManualPlayers =
    cutSetup?.cutMode === 'MANUAL' && type === 'player' && cutSetup?.players?.length === 0;
  const noManualTeams =
    cutSetup?.cutMode === 'MANUAL' && type === 'team' && cutSetup?.teams?.length === 0;

  const noCut = !cutSetup || noAutoTeams || noAutoPlayers || noManualPlayers || noManualTeams;

  if (noCut) {
    return { isCut: () => false, isFirstCutRow: () => false, cutStatus: 'projected' };
  }

  const cutSelection = useMemo(() => {
    if (cutSetup?.cutMode === 'AUTO') {
      if (type === 'team') {
        return leaderboardData
          ?.filter((x) => (x.currentRank ?? 0) > (cutSetup?.nTopTeams ?? 0))
          .map((x) => x.schoolId);
      } else {
        return leaderboardData
          ?.filter((x) => (x.currentRank ?? 0) > (cutSetup?.nTopPlayers ?? 0))
          .map((x) => x.playerId);
      }
    } else {
      if (type === 'team') {
        if (cutSetup?.teams?.length === 0) {
          return leaderboardData?.map((x) => x.schoolId);
        }
        return leaderboardData
          ?.filter((x) => !cutSetup?.teams?.includes(x.schoolId ?? ''))
          .map((x) => x.schoolId);
      } else {
        if (cutSetup?.players?.length === 0) {
          return leaderboardData?.map((x) => x.playerId);
        }
        return leaderboardData
          ?.filter((x) => !cutSetup?.players?.includes(x.playerId ?? ''))
          .map((x) => x.playerId);
      }
    }
  }, [leaderboardData, cutSetup]);

  const cutIsFinal = useMemo(() => {
    if (!cutSetup) return false;
    const nonFinalTeams = leaderboardData?.filter(
      (x) =>
        (x.currentRound === cutSetup.cutAfter && Number(x.holeThrough) !== 18) ||
        (x.currentRound || 0) < (cutSetup?.cutAfter ?? 0)
    );
    return nonFinalTeams?.length === 0;
  }, [cutSetup, leaderboardData]);

  const isCut = useCallback(
    (rowData: ElasticRoundAnalyticsWithAveragePoints) => {
      return Boolean(
        cutSelection?.includes(rowData.playerId ?? '') ||
          cutSelection?.includes(rowData.schoolId ?? '')
      );
    },
    [cutSelection]
  );

  const isFirstCutRow = useCallback(
    (rowData: ElasticRoundAnalyticsWithAveragePoints) => {
      if (type === 'team' && (cutSetup?.nTopTeams ?? 0) > 0) {
        return Boolean(cutSetup && cutSelection?.[0] === rowData.schoolId);
      } else if (type === 'player' && (cutSetup?.nTopPlayers ?? 0) > 0) {
        return Boolean(cutSetup && cutSelection?.[0] === rowData.playerId);
      }
      return false;
    },
    [cutSetup, cutIsFinal, isCut]
  );

  return { isCut, isFirstCutRow, cutStatus: cutIsFinal ? 'final' : 'projected' };
};
